import { useCallback, useEffect, useRef, useState } from 'react';
import { Heading, HStack, InputGroup, Text, VStack } from '@chakra-ui/react';
import { useDisplayPrice } from '../../hooks/useDisplayPrice';
import { RiftboundInput } from '../ui/input';
import { commitPriceBounds, formatPriceBound, parsePriceDraft } from '../../utils/priceRange';

interface Props {
  priceMin?: number;
  priceMax?: number;
  onChange: (next: { priceMin?: number; priceMax?: number }) => void;
  showLabel?: boolean;
}

export function PriceRangeFilter({
  priceMin,
  priceMax,
  onChange,
  showLabel = true,
}: Props) {
  const { currency } = useDisplayPrice();
  const symbol = currencySymbol(currency);
  const [minText, setMinText] = useState(() => formatPriceBound(priceMin));
  const [maxText, setMaxText] = useState(() => formatPriceBound(priceMax));
  const minRef = useRef(minText);
  const maxRef = useRef(maxText);
  const minBoundRef = useRef(priceMin);
  const maxBoundRef = useRef(priceMax);
  const onChangeRef = useRef(onChange);
  minRef.current = minText;
  maxRef.current = maxText;
  minBoundRef.current = priceMin;
  maxBoundRef.current = priceMax;
  onChangeRef.current = onChange;

  useEffect(() => {
    const parsed = parsePriceDraft(minRef.current);
    if (parsed === 'partial') return;
    if (parsed !== priceMin) setMinText(formatPriceBound(priceMin));
  }, [priceMin]);

  useEffect(() => {
    const parsed = parsePriceDraft(maxRef.current);
    if (parsed === 'partial') return;
    if (parsed !== priceMax) setMaxText(formatPriceBound(priceMax));
  }, [priceMax]);

  const publish = useCallback((swap: boolean) => {
    const next = commitPriceBounds(minRef.current, maxRef.current, swap);
    if (!next.complete) return;
    if (next.minText !== minRef.current) setMinText(next.minText);
    if (next.maxText !== maxRef.current) setMaxText(next.maxText);
    if (next.priceMin === minBoundRef.current && next.priceMax === maxBoundRef.current) return;
    onChangeRef.current({ priceMin: next.priceMin, priceMax: next.priceMax });
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => publish(false), 300);
    return () => window.clearTimeout(timer);
  }, [minText, maxText, publish]);

  return (
    <VStack align="stretch" gap={2} w="100%">
      {showLabel && (
        <Heading as="h3" size="sm">Price</Heading>
      )}
      <HStack gap={2}>
        <PriceField
          label="Minimum price"
          placeholder="Min"
          symbol={symbol}
          value={minText}
          onChange={setMinText}
          onBlur={() => publish(true)}
        />
        <Text fontSize="xs" color="textMuted">to</Text>
        <PriceField
          label="Maximum price"
          placeholder="Max"
          symbol={symbol}
          value={maxText}
          onChange={setMaxText}
          onBlur={() => publish(true)}
        />
      </HStack>
    </VStack>
  );
}

function PriceField({
  label,
  placeholder,
  symbol,
  value,
  onChange,
  onBlur,
}: {
  label: string;
  placeholder: string;
  symbol: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  return (
    <InputGroup
      flex="1"
      startElement={<Text fontSize="xs" color="textMuted">{symbol}</Text>}
    >
      <RiftboundInput
        aria-label={label}
        placeholder={placeholder}
        inputMode="decimal"
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
    </InputGroup>
  );
}

function currencySymbol(code: string): string {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0).find((part) => part.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}
